import React, { useState, useEffect, useCallback } from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { useSalesPortalOpportunities } from "../../hooks/useSalesPortalOpportunities";
import { OpportunityList } from "../OpportunityList/OpportunityList";
import { OpportunityDetail } from "../OpportunityDetail/OpportunityDetail";
import { OpportunitySearch } from "../OpportunitySearch/OpportunitySearch";

function SalesPortalPage() {
  const [activeTab, setActiveTab] = useState("my-opportunities");
  const [hasSearched, setHasSearched] = useState(false);
  const {
    opportunities,
    selectedIndex,
    setSelectedIndex,
    loading,
    loadMyOpportunities,
    searchOpportunities,
    clearOpportunities,
  } = useSalesPortalOpportunities();

  useEffect(() => {
    loadMyOpportunities();
  }, [loadMyOpportunities]);

  const handleTabChange = useCallback(
    (value: string) => {
      setActiveTab(value);
      if (value === "my-opportunities") {
        setHasSearched(false);
        loadMyOpportunities();
      } else {
        setHasSearched(false);
        clearOpportunities();
      }
    },
    [loadMyOpportunities, clearOpportunities],
  );

  const handleSearch = useCallback(
    (email: string, mode: "prospect" | "salesperson") => {
      setHasSearched(true);
      searchOpportunities(email, mode);
    },
    [searchOpportunities],
  );

  const selectedOpportunity =
    opportunities.length > 0 ? opportunities[selectedIndex] : null;

  const showEmptyState = opportunities.length === 0 && !loading;

  const emptyStateMessage =
    activeTab === "my-opportunities"
      ? "No opportunities found"
      : hasSearched
        ? "No results found"
        : "Search for opportunities by email";

  return (
    <MainPageWrapper title="Opportunities">
      <Tabs onValueChange={handleTabChange} value={activeTab}>
        <TabsList>
          <TabsTrigger value="my-opportunities">My Opportunities</TabsTrigger>
          <TabsTrigger value="search">Search</TabsTrigger>
        </TabsList>

        <TabsContent value="search">
          <OpportunitySearch loading={loading} onSearch={handleSearch} />
        </TabsContent>
      </Tabs>

      <div className="mt-4">
        {loading && (
          <div className="flex items-center justify-center min-h-[300px]">
            <LoadingIndicator isFullScreen={false} />
          </div>
        )}

        {showEmptyState && (
          <div className="flex items-center justify-center min-h-[300px]">
            <p className="text-sm text-gray-400 dark:text-gray-500">
              {emptyStateMessage}
            </p>
          </div>
        )}

        {!loading && opportunities.length > 0 && (
          <div className="flex h-[calc(100vh-220px)] min-h-[500px] border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800">
            <div className="w-[35%] border-r border-gray-200 dark:border-slate-700 overflow-y-auto">
              <OpportunityList
                onSelect={setSelectedIndex}
                opportunities={opportunities}
                selectedIndex={selectedIndex}
              />
            </div>
            <div className="w-[65%] overflow-y-auto">
              <OpportunityDetail opportunity={selectedOpportunity} />
            </div>
          </div>
        )}
      </div>
    </MainPageWrapper>
  );
}

export { SalesPortalPage };
export default SalesPortalPage;
